import React from 'react';
import { motion } from 'framer-motion';

interface RequestAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RequestAccessModal: React.FC<RequestAccessModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const subject = 'Portfolio Password Request';

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  React.useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');

    try {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? '/.netlify/functions/contact'
        : 'http://localhost:9999/.netlify/functions/contact';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject,
          message: formData.message || 'Hello, I would like to request the password to view your portfolio.'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="request-access-title"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
          aria-label="Close modal"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 md:px-8 md:pt-8 pb-4">
            <h2 id="request-access-title" className="text-2xl font-bold text-gray-900 mb-2">
              Request Access
            </h2>
            <p className="text-gray-600 mb-6">
              Send a quick request to receive the password for the portfolio.
            </p>

            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-[#89ABE3] focus:ring-[#89ABE3] disabled:opacity-50 disabled:pointer-events-none"
                  placeholder="Your name"
                />
              </div>

              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-[#89ABE3] focus:ring-[#89ABE3] disabled:opacity-50 disabled:pointer-events-none"
                  placeholder="Your email"
                />
              </div>

              <div className="relative">
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-[#89ABE3] focus:ring-[#89ABE3] disabled:opacity-50 disabled:pointer-events-none"
                  placeholder="Optional message"
                ></textarea>
              </div>

              {status === 'success' && (
                <div className="p-3 bg-green-100 text-green-700 rounded-lg text-sm">
                  Request sent! I’ll get back to you shortly.
                </div>
              )}
              {status === 'error' && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={status === 'sending'}
              className={`w-full py-3 text-sm font-semibold text-white ${
                status === 'sending' 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-[#638fd8] hover:bg-[#7B9AD2]'
              } transition-colors duration-300 rounded-b-xl flex items-center justify-center uppercase tracking-wider`}
            >
              {status === 'sending' ? 'Sending...' : 'Send Request'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default RequestAccessModal;


